import type { Cadence, Recording, SessionPlan } from '../types.ts';
import { composeSession, type CompositionFailure, type TargetInterval } from './composition.ts';
import { planDiagnostics, type PlanDiagnostics, type PlanOptions, type SessionReach } from './plan.ts';
export interface SessionRequest extends PlanOptions {
  recording: Recording; targetSec: number; cadence: Cadence;
  /** Only QA may inspect withheld content. Band and editorial protections still apply. */
  includeWithheld?: boolean;
}
export type EligibilityReason = CompositionFailure;
export type SessionEvaluation =
  | {eligible:true;status:'conforming';reason:null;reach:SessionReach;plan:SessionPlan;diagnostics:PlanDiagnostics;feasibleTargets:TargetInterval[]}
  | {eligible:false;status:'unavailable';reason:EligibilityReason;reach:SessionReach|null;plan:null;detail:string;feasibleTargets:TargetInterval[]};
export function evaluateSession(request:SessionRequest):SessionEvaluation {
  const {recording,targetSec,cadence,...opts}=request;
  const result=composeSession(recording,targetSec,cadence,opts);
  const intervals=result.feasibleTargets;
  const reach=intervals.length?{minSec:Math.min(...intervals.map(i=>i.minSec)),maxSec:Math.max(...intervals.map(i=>i.maxSec))}:null;
  if(result.status==='unavailable')return {eligible:false,status:'unavailable',reason:result.reason,reach,plan:null,detail:result.detail,feasibleTargets:intervals};
  return {eligible:true,status:'conforming',reason:null,reach:reach!,plan:result.plan,
    diagnostics:planDiagnostics(result.plan,recording),feasibleTargets:intervals};
}
export function isEligible(request:SessionRequest):boolean { return evaluateSession(request).eligible; }
export class IneligibleSessionError extends Error {
  readonly evaluation: Extract<SessionEvaluation,{eligible:false}>;
  constructor(evaluation:Extract<SessionEvaluation,{eligible:false}>){super(evaluation.detail);this.name='IneligibleSessionError';this.evaluation=evaluation;}
}
export function assertEligible(request:SessionRequest):Extract<SessionEvaluation,{eligible:true}>{
  const result=evaluateSession(request);if(!result.eligible)throw new IneligibleSessionError(result);return result;
}
